import { TFunction } from 'i18next';
import { timestampFilter } from 'v5/core/standard-date-filter/timestamp-filter';
import { MergeServices } from 'v5/platform/ts/services';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { createOwnerFilterConfig } from '../../ownerFilterConfig';
import { FilterMetastructureRequest } from '../../useMetastructureFilters';
import { createAgentFilterConfig } from './agent';
import { createEmailsFilterConfig } from './emails';
import { CORE_FIELD_NAMES, CoreFieldName } from './fields';
import { createGroupsFilterConfig } from './groups';
import { createLocationsFilterConfig } from './locations';
import { createOrganizationFilterConfig } from './organization';
import { createPhonesFilterConfig } from './phones';
import { createProductionArchivedFilterConfig } from './production-archived';
import { createProductionBudgetAmountFilterConfig } from './production-budget-amount';
import { createProductionCodeFilterConfig } from './production-code';
import { createProductionColorFilterConfig } from './production-color';
import { createProductionDateFilterConfig } from './production-date';
import { createProductionDescriptionFilterConfig } from './production-description';
import { createProductionIconFilterConfig } from './production-icon';
import { createProductionStatusFilterConfig } from './production-status';
import { createProductionTagsFilterConfig } from './production-tags';
import { createProductionTitleFilterConfig } from './production-title';
import { createResourceTagsFilterConfig } from './resource-tags';
import { createResourceTypeFilterConfig } from './resource-type';
import { createRoleFilterConfig } from './role';
import { createSocialsFilterConfig } from './socials';
import { createWebsitesFilterConfig } from './websites';
import { FilterConfigBase } from 'v5/patterns/filters/FilterWidget.types';

export function createCoreFieldFilterConfig(
  t: TFunction<'core'>,
  fieldName: CoreFieldName,
  request: FilterMetastructureRequest,
  services: MergeServices<
    [
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService
    ]
  >
): FilterConfigBase | null {
  // Core field configurations based on field name
  switch (fieldName) {
    // Production fields
    case CORE_FIELD_NAMES.STATUS:
      return createProductionStatusFilterConfig();
    case CORE_FIELD_NAMES.PRODUCTION_DATE:
      return createProductionDateFilterConfig(t);
    case CORE_FIELD_NAMES.CODE:
      return createProductionCodeFilterConfig();
    case CORE_FIELD_NAMES.ARCHIVED:
      return createProductionArchivedFilterConfig();
    case CORE_FIELD_NAMES.USER_ID:
      return createOwnerFilterConfig();
    case CORE_FIELD_NAMES.PRODUCTION_TYPE_ID:
      return null; // Handled separately in production filters
    case CORE_FIELD_NAMES.ICON:
      return createProductionIconFilterConfig();
    case CORE_FIELD_NAMES.COLOR:
      return createProductionColorFilterConfig();
    case CORE_FIELD_NAMES.TITLE:
      return createProductionTitleFilterConfig();
    case CORE_FIELD_NAMES.DESCRIPTION:
      return createProductionDescriptionFilterConfig();
    case CORE_FIELD_NAMES.BUDGET_AMOUNT:
      return createProductionBudgetAmountFilterConfig();

    // Resource fields
    case CORE_FIELD_NAMES.RESOURCE_TYPE_ID:
      return createResourceTypeFilterConfig(services, request.scope);
    case CORE_FIELD_NAMES.ORGANISATION_ID:
      return createOrganizationFilterConfig(services, request.scope);
    case CORE_FIELD_NAMES.REPRESENTED_BY_ID:
      return createAgentFilterConfig(services, request.scope);
    case CORE_FIELD_NAMES.ROLE:
      return createRoleFilterConfig();
    case CORE_FIELD_NAMES.TAGS:
      switch (request.type) {
        case 'production':
          return createProductionTagsFilterConfig(services);
        case 'resource':
          return createResourceTagsFilterConfig(services);
        default:
          return null;
      }
    case CORE_FIELD_NAMES.GROUPS:
      return createGroupsFilterConfig(services);

    case CORE_FIELD_NAMES.EMAILS:
      return createEmailsFilterConfig();
    case CORE_FIELD_NAMES.PHONES:
      return createPhonesFilterConfig();
    case CORE_FIELD_NAMES.WEBSITES:
      return createWebsitesFilterConfig();
    case CORE_FIELD_NAMES.SOCIALS:
      return createSocialsFilterConfig();
    case CORE_FIELD_NAMES.LOCATIONS:
      return createLocationsFilterConfig();

    // Common fields
    case CORE_FIELD_NAMES.CREATED_AT:
    case CORE_FIELD_NAMES.UPDATED_AT:
      return timestampFilter.createFilterConfig(
        t(`core_fields.${fieldName}`),
        t
      );

    default:
      return null; // Not all core fields are filterable
  }
}
